<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { useGlobalStore } from "@stores/global";
import IconX from "@components/icons/X.vue";
import IconAchivement from "@components/icons/Achivement.vue";

const DURATION_MS = 5000;

const globalStore = useGlobalStore();
const progress = ref(100);
let timer = null;
let rafId = null;

function cleanup() {
    clearTimeout(timer);
    timer = null;
    cancelAnimationFrame(rafId);
    rafId = null;
}

function startCountdown() {
    cleanup();
    const start = performance.now();
    const tick = (now) => {
        const elapsed = now - start;
        progress.value = Math.max(0, 100 - (elapsed / DURATION_MS) * 100);
        if (elapsed < DURATION_MS) {
            rafId = requestAnimationFrame(tick);
        }
    };
    rafId = requestAnimationFrame(tick);
    timer = setTimeout(() => {
        globalStore.hideAchievement();
    }, DURATION_MS);
}

watch(
    () => globalStore.achievement.active,
    (active) => {
        if (active) {
            progress.value = 100;
            startCountdown();
        } else {
            cleanup();
        }
    },
);

const isMobile = useMediaQuery("(max-width: 767px)");

const rightEdgeColumn = computed(() => {
    if (globalStore.panelActive("countryInfo")) return -7;
    if (globalStore.panelActive("trivia")) return -6;
    const page = globalStore.activePage;
    if (page === "achievements" || page === "settings" || page === "vote")
        return -6;
    return -1;
});

const toastStyle = computed(() =>
    isMobile.value ? {} : { gridColumn: `span 5 / ${rightEdgeColumn.value}` },
);

onUnmounted(() => {
    cleanup();
});

function close() {
    globalStore.hideAchievement();
}
</script>

<template>
    <Transition name="toast">
        <div
            v-if="globalStore.achievement.active && !globalStore.isLanding"
            class="achievement-toast bg-marine-700 relative isolate flex items-center gap-4 p-6 pb-7"
            :style="toastStyle"
        >
            <!-- Close -->
            <button
                class="absolute top-2 right-2 flex size-6 items-center justify-center text-yellow-400"
                @click="close"
            >
                <span class="sr-only">Close</span>
                <IconX class="size-4" />
            </button>

            <!-- Badge -->
            <div
                class="relative isolate flex size-[72px] shrink-0 items-center justify-center"
            >
                <IconAchivement class="text-yellow-400" />
                <i
                    v-if="globalStore.achievement.icon"
                    :class="[
                        globalStore.achievement.icon,
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl text-[24px] text-yellow-400',
                    ]"
                ></i>
            </div>

            <!-- Text -->
            <div class="flex min-w-0 flex-1 flex-col justify-center gap-2 pr-4">
                <p class="body-lg leading-[0.9] font-bold uppercase">
                    {{ globalStore.achievement.title }}
                </p>
                <p class="body-sm leading-[1.2] faded-text">
                    {{ globalStore.achievement.description }}
                </p>
            </div>

            <div
                class="bg-marine-600 absolute right-0 bottom-0 left-0 -z-1 h-1 overflow-clip"
            >
                <div
                    class="h-full bg-yellow-400"
                    :style="{ width: `${progress}%` }"
                ></div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.achievement-toast {
    grid-row: 2 / -1;
    align-self: start;
    height: auto;
    z-index: 5;
}

@media (max-width: 767px) {
    .achievement-toast {
        grid-column: 1 / -1;
        grid-row: 1 / 2;
        z-index: 100;
    }
}

.toast-enter-active,
.toast-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
